/**
 * Per-champion GROUND MARKER for the 3D character path (#825) — the on-floor
 * "where is this body and which way is it looking" readout that the 2D
 * renderer draws (`entityView.ts`: a filled body circle at the LIVE collision
 * radius + a `FACING_TICK` line) and that `mount.ts`'s `use3D` deliberately
 * suppresses for a champion that resolved a GLB (#237/#240). Without this
 * module, opting a champion into 3D silently DELETES both readouts.
 *
 * ── Why it lives in the THREE scene, not in Pixi ─────────────────────────────
 * The requested read is "the ring is on the ground, the unit stands IN it, and
 * the unit's body hides the FAR arc". That is a per-pixel depth interaction
 * between the marker and the character mesh, so it is only expressible inside
 * the Three pass: the arena floor is Pixi (`stage.ts` `floor`/`ground` under
 * `worldBelow`), and a Pixi-drawn marker can only be WHOLLY behind the entire
 * 3D pass (`worldBelow`) or WHOLLY in front of it (`overlayAbove`).
 * `propModel.ts` already proves the trick with the roles swapped: pillars share
 * the characters' scene so the GPU depth buffer occludes a champion standing
 * behind one (#816, `propOcclusion.test.ts`). Same shared depth buffer here.
 *
 * ── The shape (decided in #825, not free for the implementer) ────────────────
 *   • An OUTLINED RING, not a filled disc — it reads over the textured arena
 *     floor without muddying the character silhouette.
 *   • The facing indicator lives INSIDE the rim, as a wedge spanning exactly
 *     one facing sector. This diverges from the 2D tick (which points OUTWARD
 *     to a fixed `FACING_TICK_LENGTH` px) ON PURPOSE: in 3D an outward tip
 *     points away from the camera for a unit facing "up"-screen and is hidden
 *     by the model — i.e. it fails exactly when the player needs it. Inside the
 *     rim can never be occluded by the unit's own feet, and being sized as a
 *     FRACTION of the live radius it stays legible at any zoom.
 *   • Team colour comes from `teamColor(entity.team)` — the same palette the
 *     2D body fill uses. No new colours.
 *   • A DEAD unit has NO marker (see `reconcileGroundMarkers`).
 *
 * ── Depth setup (the actual technical work) ──────────────────────────────────
 * The marker sits on the plane the character's feet are grounded to
 * (`normaliseToPlacementRoot` puts feet at local y = 0), so it must draw over
 * the Pixi floor (which is not in the depth buffer at all — Three clears
 * depth/stencil each frame, keeping only the colour) while being DEPTH-TESTED
 * against the character mesh. That means, per material:
 *   `depthTest: true`   — the far arc loses to the body's already-written depth.
 *   `depthWrite: false` — a flat decal must not stamp the depth buffer and
 *                         fight with the feet resting on the same plane.
 *   `polygonOffset`     — pulls the coplanar decal a hair toward the camera,
 *                         the standard z-fighting fix, belt-and-braces with
 *                         {@link GROUND_MARKER_LIFT_UNITS}.
 *   `renderOrder`       — drawn after the opaque characters, so the depth
 *                         buffer is fully populated before the decal is tested.
 *
 * EXTENSION POINT: later ground decals (selection rings, range circles, AoE
 * telegraphs — all out of scope for #825) should be added HERE as further
 * geometry/marker kinds rather than as a second decal system, so they inherit
 * this depth setup instead of re-deriving it.
 *
 * PRESENTATION ONLY — never reads or writes sim state or any hash. Floats are
 * legal; nothing here is hashed.
 */

import * as THREE from "three";
import { teamColor } from "../colors.js";
import { DEFAULT_BODY_RADIUS_UNITS, FACING_SECTORS } from "../projection.js";
import { computeLiveRadiusScale } from "./characterModel.js";
import { simToWorld3 } from "./isoCamera.js";

/** Scene-graph node name of the ring outline (also the test handle). */
export const GROUND_MARKER_RING_NAME = "GroundMarkerRing";
/** Scene-graph node name of the facing wedge (also the test handle). */
export const GROUND_MARKER_FACING_NAME = "GroundMarkerFacing";

/**
 * How far above the floor plane the decal sits, in SIM units (a champion stands
 * `CHARACTER_TARGET_HEIGHT_UNITS` tall — 180 since #858 — so this is well under
 * 1% of body height: invisible, but enough that the marker is never exactly
 * coplanar with a foot resting at y = 0).
 */
export const GROUND_MARKER_LIFT_UNITS = 0.5;

/** Drawn after the opaque characters, so their depth is already written. */
export const GROUND_MARKER_RENDER_ORDER = 1;

/** Ring outline thickness, as a fraction of the marker radius (inner edge). */
export const RING_INNER_FRACTION = 0.82;
/** Facing wedge inner edge, as a fraction of the marker radius. */
export const FACING_INNER_FRACTION = 0.45;
/** Facing wedge outer edge — strictly INSIDE the ring's inner edge, with a
 *  visible gap, so the indicator reads as "in the circle" rather than as a
 *  bump on it. */
export const FACING_OUTER_FRACTION = 0.72;

/** Decal opacity — solid enough to read on the arena floor, light enough not
 *  to compete with the character silhouette. */
export const GROUND_MARKER_OPACITY = 0.85;

/** Arc resolution of the ring (smooth at the largest sane zoom). */
const RING_SEGMENTS = 64;
/** Arc resolution of the (22.5°) facing wedge. */
const FACING_SEGMENTS = 8;

/**
 * Geometry is authored in the XY plane (Three's ring primitives are), then
 * baked flat into the XZ ground plane by rotating −90° about X. That maps a
 * geometry point at polar angle φ to world (cos φ, 0, −sin φ) and leaves the
 * surface normal pointing UP (+Y). Baking it into the geometry (rather than
 * onto the mesh) keeps every mesh at an identity local transform, so the
 * marker group's own position/rotation/scale are the ONLY transform in play.
 */
const FLATTEN_X = -Math.PI / 2;

/**
 * Geometry-space angle whose baked world direction is +Z — the direction a
 * character model faces at yaw 0 (`facingToYaw`: "model forward is +Z at yaw
 * 0"). Solving −sin φ = 1 gives φ = −π/2. This is what ties the wedge to the
 * model's forward axis: rotate both by the same yaw and they cannot disagree.
 */
const FORWARD_PHI = -Math.PI / 2;

/** The wedge spans exactly ONE facing sector (360° / 16 = 22.5°), so the
 *  indicator's width IS the sim's angular resolution — not a made-up angle. */
const FACING_ARC = (2 * Math.PI) / FACING_SECTORS;

/**
 * Unit-radius (radius 1) ring outline, flat in XZ. Shared by every marker
 * instance — the same "N instances, one upload" rule `instantiateProp` follows
 * — so `disposeGroundMarker` deliberately does NOT dispose it.
 */
const RING_GEOMETRY: THREE.BufferGeometry = new THREE.RingGeometry(
  RING_INNER_FRACTION,
  1,
  RING_SEGMENTS,
).rotateX(FLATTEN_X);

/** Unit-radius facing wedge, flat in XZ, centred on the model's forward axis. */
const FACING_GEOMETRY: THREE.BufferGeometry = new THREE.RingGeometry(
  FACING_INNER_FRACTION,
  FACING_OUTER_FRACTION,
  FACING_SEGMENTS,
  1,
  FORWARD_PHI - FACING_ARC / 2,
  FACING_ARC,
).rotateX(FLATTEN_X);

/** Where + how big one marker sits, in SIM units. */
export interface GroundMarkerPlacement {
  /** Marker centre on the sim ground plane (the entity's interpolated pos). */
  readonly wx: number;
  readonly wy: number;
  /** The entity's LIVE, aura-modified body radius (#342) — never baked art,
   *  never `DEFAULT_BODY_RADIUS_UNITS`, so a size statMod visibly resizes the
   *  marker exactly as it resizes the model. */
  readonly radius: number;
  /** Y rotation in radians. The caller passes the character instance's OWN
   *  (lerped) yaw, so the marker and the model can never disagree about which
   *  way the unit is looking — see `threeLayer.ts`'s `facingToYaw`. */
  readonly yaw: number;
}

/** One entity's marker state for the per-frame reconcile. */
export interface GroundMarkerSubject extends GroundMarkerPlacement {
  /** Entity id — the reconcile key, same as `Character3DInput.id`. */
  readonly id: string;
  /** Team index, for `teamColor`. Read once, when the marker is created. */
  readonly team: number;
  /** A DEAD unit keeps no marker (#825): the marker exists to read the
   *  position + facing of a LIVE threat, so a corpse keeping one is clutter
   *  that muddies "who is still alive" at a glance. Deliberately divergent
   *  from the 2D path, which dims the whole entity via `DEAD_ALPHA` instead. */
  readonly alive: boolean;
}

/** The minimal `THREE.Scene` surface the reconcile needs — declared
 *  structurally so the lifecycle is unit-testable against a plain fake. */
export interface GroundMarkerHost {
  add: (object: THREE.Object3D) => unknown;
  remove: (object: THREE.Object3D) => unknown;
}

/**
 * The marker's world radius in sim units for a live entity `radius`.
 *
 * Deliberately routed through `computeLiveRadiusScale` — the SAME derivation
 * the 3D model's live scale (#342) uses — so a valid radius maps to itself
 * EXACTLY and a degenerate one degrades to the default body radius, which is
 * exactly the size the model degrades to (scale 1) for the same input. Marker
 * and model therefore stay the same size even in the failure case.
 *
 * The trailing check is `propModel.safeScale`'s discipline: 0 / negative / NaN
 * are already absorbed by `computeLiveRadiusScale`'s own guard, and this also
 * catches a non-finite radius (which that guard passes straight through),
 * so no input can produce a collapsed, inverted or NaN transform. Pure.
 */
export function groundMarkerRadiusUnits(radius: number): number {
  const units = computeLiveRadiusScale(radius) * DEFAULT_BODY_RADIUS_UNITS;
  return units > 0 && Number.isFinite(units) ? units : DEFAULT_BODY_RADIUS_UNITS;
}

/** Non-finite rotations would poison the whole matrix (and everything parented
 *  to it) with NaN, so a bad yaw degrades to 0 rather than to a broken node. */
function safeYaw(yaw: number): number {
  return Number.isFinite(yaw) ? yaw : 0;
}

/**
 * Build one entity's marker: a ring outline + an inside-the-rim facing wedge,
 * both unit-radius and flat on the ground, under a single group whose
 * transform is set by {@link updateGroundMarker}. Both meshes share ONE
 * material (same colour, same depth setup), which is the only per-instance GPU
 * resource and the only thing `disposeGroundMarker` frees.
 */
export function createGroundMarker(team: number): THREE.Group {
  const material = new THREE.MeshBasicMaterial({
    color: teamColor(team),
    transparent: true,
    opacity: GROUND_MARKER_OPACITY,
    // Depth: tested against the characters (so the far arc is hidden by the
    // body) but never written (so a decal resting on the feet plane cannot
    // z-fight with the feet, or with a second decal added later).
    depthTest: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
    // Flat, unlit, and visible from either side: the camera is always above,
    // but a decal that silently vanishes if the plane is ever flipped is a
    // failure mode with no upside.
    side: THREE.DoubleSide,
  });

  const ring = new THREE.Mesh(RING_GEOMETRY, material);
  ring.name = GROUND_MARKER_RING_NAME;
  ring.renderOrder = GROUND_MARKER_RENDER_ORDER;

  const facing = new THREE.Mesh(FACING_GEOMETRY, material);
  facing.name = GROUND_MARKER_FACING_NAME;
  facing.renderOrder = GROUND_MARKER_RENDER_ORDER;

  const root = new THREE.Group();
  root.name = "GroundMarker";
  root.add(ring, facing);
  return root;
}

/**
 * Place/resize/orient an existing marker for this frame. Uniform scale is safe
 * because the geometry is flat (zero Y extent), and the lift is applied to the
 * POSITION so scaling can never change how far off the floor the decal sits.
 */
export function updateGroundMarker(marker: THREE.Object3D, placement: GroundMarkerPlacement): void {
  marker.position.copy(simToWorld3(placement.wx, placement.wy, GROUND_MARKER_LIFT_UNITS));
  marker.rotation.set(0, safeYaw(placement.yaw), 0);
  marker.scale.setScalar(groundMarkerRadiusUnits(placement.radius));
}

/**
 * Free the per-instance GPU resource. Geometry is SHARED with every other
 * marker (module-level unit geometry) and is deliberately not disposed.
 */
export function disposeGroundMarker(marker: THREE.Object3D): void {
  marker.traverse((child) => {
    // `Mesh.material` is loosely typed (`any`) in @types/three; narrow through
    // `unknown` rather than trusting it.
    const material: unknown = child instanceof THREE.Mesh ? child.material : null;
    if (material instanceof THREE.Material) material.dispose();
  });
}

/**
 * Reconcile the live marker set against this frame's subjects — the marker
 * half of `threeLayer.syncCharacters`'s reconcile loop, extracted so the
 * LIFECYCLE (which is where the dead-unit rule lives) is unit-testable without
 * a renderer.
 *
 * ONE removal path, three ways in:
 *   • the entity left the frame (absent from `subjects` — despawned, or
 *     stealth-hidden by #422's filter in `mount.ts`),
 *   • the entity DIED (`alive: false` is treated exactly like "left the
 *     frame" — the deliberate #825 divergence from the 2D `DEAD_ALPHA` dim),
 *   • teardown (`subjects: []`, which is how `threeLayer.destroy` clears up).
 *
 * `markers` is mutated in place; iteration order is Map insertion order, which
 * is presentational bookkeeping only (the GPU resolves depth per-pixel).
 */
export function reconcileGroundMarkers(
  markers: Map<string, THREE.Object3D>,
  host: GroundMarkerHost,
  subjects: readonly GroundMarkerSubject[],
): void {
  const seen = new Set<string>();
  for (const s of subjects) {
    if (!s.alive) continue;
    seen.add(s.id);
    let marker = markers.get(s.id);
    if (marker === undefined) {
      marker = createGroundMarker(s.team);
      host.add(marker);
      markers.set(s.id, marker);
    }
    updateGroundMarker(marker, s);
  }
  for (const [id, marker] of markers) {
    if (seen.has(id)) continue;
    host.remove(marker);
    disposeGroundMarker(marker);
    markers.delete(id);
  }
}
